import {
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import useShopOrdersViewState from "./useShopOrdersViewState";
import ShopOrdersFiltersBar from "./ShopOrdersFiltersBar";
import ShopOrdersDesktopTable from "./ShopOrdersDesktopTable";
import ShopOrdersMobileList from "./ShopOrdersMobileList";
import ShopConfirmDialog from "./ShopConfirmDialog";
import ShopOrderEditDialog from "./ShopOrderEditDialog";
import { toCurrency } from "./shopUtils";

export default function ShopOrdersTab({
  adminOrders,
  loadingAdminOrders,
  adminOrderStats,
  adminMonth,
  setAdminMonth,
  orderStatusFilter,
  setOrderStatusFilter,
  selectedOrderIds,
  setSelectedOrderIds,
  onRefetch,
  onOrderStatusChange,
  onCancelOrders,
  onDeleteOrders,
  onBulkStatusChange,
  onExportSepa,
  exportingSepa,
  runningAdminAction,
  products,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const vs = useShopOrdersViewState({
    adminOrders,
    adminMonth,
    orderStatusFilter,
    selectedOrderIds,
    setSelectedOrderIds,
    onCancelOrders,
    onDeleteOrders,
    onBulkStatusChange,
    onOrderStatusChange,
    runningAdminAction,
    products,
  });

  const renderEmptyState = () => (
    <Paper
      variant="outlined"
      sx={{ p: 5, textAlign: "center", borderRadius: 2 }}
    >
      <Iconify
        icon="mdi:receipt-text-outline"
        width={72}
        sx={{ color: "grey.300", mb: 2 }}
      />
      <Typography variant="body1" color="text.secondary" fontWeight={500}>
        {vs.hasSearchNoResults
          ? "Keine Treffer für die aktuelle Suche."
          : "Keine Bestellungen für die aktuelle Filterung vorhanden."}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {vs.hasSearchNoResults
          ? "Passe den Suchbegriff an oder entferne ihn, um wieder Ergebnisse anzuzeigen."
          : "Ändere den Monat oder Status-Filter, um weitere Bestellungen anzuzeigen."}
      </Typography>
    </Paper>
  );

  const renderSkeleton = () => (
    <Stack spacing={1.5}>
      {Array.from({ length: 3 }).map((_, index) => (
        <Paper
          key={`skel-${index}`}
          variant="outlined"
          sx={{ p: 2, borderRadius: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="rectangular" width={24} height={24} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton width="50%" height={22} />
              <Skeleton width="30%" height={16} />
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );

  return (
    <Stack spacing={2.5}>
      {/* ── Stats header ── */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2.5,
          background:
            "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(14,165,233,0.08) 100%)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Bestellungen &amp; Workflow</Typography>
            <Typography variant="body2" color="text.secondary">
              Suchen, sortieren, bündeln, drucken und bearbeiten. Beim
              SEPA-Export werden exportierte Bestellungen automatisch auf
              &bdquo;Erfüllt&ldquo; gesetzt.
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Gesamt
                </Typography>
                <Typography variant="h6">{adminOrderStats.total}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Neu
                </Typography>
                <Typography variant="h6">
                  {adminOrderStats.created}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  In Bearbeitung
                </Typography>
                <Typography variant="h6">
                  {adminOrderStats.processing}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Freigegeben
                </Typography>
                <Typography variant="h6">
                  {adminOrderStats.approved}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Erfüllt
                </Typography>
                <Typography variant="h6">
                  {adminOrderStats.fulfilled}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Umsatz
                </Typography>
                <Typography variant="h6">
                  {toCurrency(adminOrderStats.revenue)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            flexWrap="wrap"
          >
            <Chip
              variant="outlined"
              label={`Sichtbar: ${vs.visibleOrders.length}`}
            />
            <Chip
              variant="outlined"
              label={`Sichtbar ausgewählt: ${vs.visibleSelectedCount}`}
            />
            <Chip
              variant="outlined"
              label={`${vs.processingScopeLabel}: ${
                vs.processingOrders.length
              } · ${toCurrency(vs.processingOrdersTotal)}`}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* ── Filters + advanced tools ── */}
      <ShopOrdersFiltersBar
        adminMonth={adminMonth}
        setAdminMonth={setAdminMonth}
        orderStatusFilter={orderStatusFilter}
        setOrderStatusFilter={setOrderStatusFilter}
        normalizedStatusFilter={vs.normalizedStatusFilter}
        fulfillmentStatusFilter={vs.fulfillmentStatusFilter}
        setFulfillmentStatusFilter={vs.setFulfillmentStatusFilter}
        searchTerm={vs.searchTerm}
        setSearchTerm={vs.setSearchTerm}
        sortBy={vs.sortBy}
        setSortBy={vs.setSortBy}
        optionalTableColumns={vs.optionalTableColumns}
        setOptionalTableColumns={vs.setOptionalTableColumns}
        processingScope={vs.processingScope}
        setProcessingScope={vs.setProcessingScope}
        pdfFields={vs.pdfFields}
        setPdfFields={vs.setPdfFields}
        onlyOpenForPreparation={vs.onlyOpenForPreparation}
        setOnlyOpenForPreparation={vs.setOnlyOpenForPreparation}
        allOrdersSelected={vs.allOrdersSelected}
        partiallySelected={vs.partiallySelected}
        onToggleSelectAll={vs.handleToggleSelectAll}
        selectedOrderIds={selectedOrderIds}
        selectedOrdersTotal={vs.selectedOrdersTotal}
        exportableSelectedOrderIds={vs.exportableSelectedOrderIds}
        onExportSepa={onExportSepa}
        exportingSepa={exportingSepa}
        onBulkActionSubmit={vs.handleBulkActionSubmit}
        onPrintOrdersPdf={vs.handlePrintOrdersPdf}
        onPrintPreparationPdf={vs.handlePrintPreparationPdf}
        runningAdminAction={runningAdminAction}
        onRefetch={onRefetch}
        visibleOrders={vs.visibleOrders}
        selectedOrders={vs.selectedOrders}
        adminOrders={adminOrders}
        processingOrders={vs.processingOrders}
        preparationOrders={vs.preparationOrders}
        preparationSummary={vs.preparationSummary}
        sortedPreparationItems={vs.sortedPreparationItems}
        preparationSortField={vs.preparationSortField}
        preparationSortDirection={vs.preparationSortDirection}
        handlePreparationSort={vs.handlePreparationSort}
        selectedPreparationKeySet={vs.selectedPreparationKeySet}
        handleTogglePreparationSelection={vs.handleTogglePreparationSelection}
        handleTogglePreparationSelectAll={vs.handleTogglePreparationSelectAll}
        allPreparationItemsSelected={vs.allPreparationItemsSelected}
        partiallySelectedPreparationItems={
          vs.partiallySelectedPreparationItems
        }
        visibleSelectedPreparationCount={vs.visibleSelectedPreparationCount}
      />

      {/* ── Orders display ── */}
      {loadingAdminOrders
        ? renderSkeleton()
        : vs.visibleOrders.length === 0
        ? renderEmptyState()
        : isMobile
        ? (
          <ShopOrdersMobileList
            visibleOrders={vs.visibleOrders}
            selectedOrderIds={selectedOrderIds}
            expandedOrderId={vs.expandedOrderId}
            onToggleOrderSelection={vs.handleToggleOrderSelection}
            onToggleExpand={vs.toggleExpand}
            onOrderStatusChange={onOrderStatusChange}
            onCancelSingleOrder={vs.handleCancelSingleOrder}
            onDeleteSingleOrder={vs.handleDeleteSingleOrder}
            onEditOrder={vs.handleEditOrder}
            onUpdateFulfillment={vs.updateOrderFulfillment}
            runningAdminAction={runningAdminAction}
          />
        )
        : (
          <ShopOrdersDesktopTable
            visibleOrders={vs.visibleOrders}
            selectedOrderIds={selectedOrderIds}
            expandedOrderId={vs.expandedOrderId}
            onToggleOrderSelection={vs.handleToggleOrderSelection}
            onToggleExpand={vs.toggleExpand}
            onOrderHeaderSort={vs.handleOrderHeaderSort}
            getOrderSortMeta={vs.getOrderSortMeta}
            onOrderStatusChange={onOrderStatusChange}
            onCancelSingleOrder={vs.handleCancelSingleOrder}
            onDeleteSingleOrder={vs.handleDeleteSingleOrder}
            onEditOrder={vs.handleEditOrder}
            onUpdateFulfillment={vs.updateOrderFulfillment}
            allOrdersSelected={vs.allOrdersSelected}
            partiallySelected={vs.partiallySelected}
            onToggleSelectAll={vs.handleToggleSelectAll}
            runningAdminAction={runningAdminAction}
          />
        )}

      {/* ── Dialogs ── */}
      <ShopConfirmDialog
        open={vs.confirmDialog.open}
        title={vs.confirmDialog.title}
        message={vs.confirmDialog.message}
        confirmLabel={vs.confirmDialog.confirmLabel}
        confirmColor={vs.confirmDialog.confirmColor}
        onConfirm={vs.confirmDialog.onConfirm}
        onCancel={vs.closeConfirm}
        loading={runningAdminAction}
      />

      <ShopOrderEditDialog
        open={Boolean(vs.editDialogOrder)}
        order={vs.editDialogOrder}
        products={products}
        onSave={vs.handleSaveEditOrder}
        onClose={() => vs.setEditDialogOrder(null)}
        saving={vs.editingOrder}
        error={vs.editOrderError}
      />
    </Stack>
  );
}
